import { useState } from "react";
import { Link } from "react-router";

import classes from "./Medik8Consultation.module.css";
import { treatmentPages, servicesData } from "./serviceData";
import BookingWidgetSection from "../../Components/BookingWidget/BookingWidgetSection";
import useDocumentMeta from "../../utils/useDocumentMeta";
import useSectionView from "../../utils/useSectionView";
import {
  BOOKING_URL,
  trackBookingClick,
  trackFaqToggle,
} from "../../utils/tracking";

import heroImage from "../../Assets/medik8_products.png";
import productImage from "../../Assets/medik8_3.jpg";

const page = treatmentPages.find((p) => p.slug === "medik8-consultation");
const service = servicesData.medik8Consultation.find(
  (s) => s.id === "medik8-consultation",
);

const steps = [
  {
    title: "Book Your Free Slot",
    text: "Reserve a 20-minute consultation online — no cost, no obligation.",
  },
  {
    title: "Skin Assessment & Chat",
    text: "We'll talk through your skin concerns, goals and current routine, and assess your skin in person.",
  },
  {
    title: "Your At-Home Plan",
    text: "You'll leave with a personalised Medik8 routine recommendation, with no pressure to purchase.",
  },
];

const faqs = [
  {
    q: "Is this consultation really free?",
    a: "Yes — there's no charge for the 20-minute consultation, and no obligation to purchase any products afterwards.",
  },
  {
    q: "Do I have to buy Medik8 products?",
    a: "Not at all. We'll recommend a routine based on your skin, and any products are available to purchase in clinic if you'd like them — but there's no pressure either way.",
  },
  {
    q: "I already use other skincare brands — can I still book?",
    a: "Of course. This consultation is a great way to get an expert opinion on your current routine, whether or not you decide to switch to Medik8.",
  },
  {
    q: "Can I have a treatment during this appointment?",
    a: "This particular consultation is for at-home product advice only, not in-clinic treatments. If you'd like a consultation that includes a treatment on the same day, ask about our Consultation & Treatment package instead.",
  },
];

const Medik8ConsultationPage = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const faqSectionRef = useSectionView("Medik8 Consultation FAQ");

  useDocumentMeta(
    `${page.title} | Radiance Aesthetics | Hindley | Wigan`,
    page.metaDescription,
  );

  return (
    <>
      {/* Hero */}
      <section className={classes.heroSection}>
        <div className={classes.heroContainer}>
          <div className={classes.heroContent}>
            <p className={classes.heroSubtitle}>
              Nurse-Led · Free Skincare Advice
            </p>
            <h1 className={classes.heroTitle}>
              Free Medik8 Product Consultation
            </h1>
            <p className={classes.heroMeta}>
              <span>Free</span> · {service.duration}
            </p>
            <p className={classes.heroDescription}>
              Not ready for a professional treatment, but want to get your
              at-home skincare routine right? Book a free consultation and we'll
              recommend a personalised Medik8 routine for your skin.
            </p>
            <a
              href={BOOKING_URL}
              className={classes.heroCta}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackBookingClick("Medik8 Consultation Hero")}
            >
              Book Your Free Consultation
            </a>
          </div>
          <div className={classes.heroImageWrapper}>
            <img
              className={classes.heroImage}
              src={heroImage}
              alt="Nurse discussing Medik8 skincare products with a client at Radiance Aesthetics"
              loading="eager"
            />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className={`${classes.section} ${classes.sectionAlt}`}>
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>How It Works</p>
          <h2 className={classes.sectionTitle}>
            Your Consultation, Step By Step
          </h2>
          <p className={classes.sectionDescription}>
            A simple, no-pressure conversation about your skin.
          </p>
          <div className={classes.stepGrid}>
            {steps.map((step, i) => (
              <div className={classes.stepCard} key={step.title}>
                <div className={classes.stepNumber}>{i + 1}</div>
                <h3 className={classes.stepTitle}>{step.title}</h3>
                <p className={classes.stepText}>{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Medik8 */}
      <section className={classes.section}>
        <div className={classes.sectionContainer}>
          <div className={classes.whyRow}>
            <img
              className={classes.whyImage}
              src={productImage}
              alt="Medik8 professional skincare products recommended at Radiance Aesthetics"
              loading="lazy"
            />
            <div className={classes.whyContent}>
              <p className={classes.sectionLabel} style={{ textAlign: "left" }}>
                Why Medik8?
              </p>
              <p className={classes.whyText}>
                We proudly use and stock Medik8 for all our treatments — rooted
                in science and driven by results, designed to deliver visible
                improvements in skin health.
              </p>
              <div>
                <span className={classes.whyBadge}>
                  Up to 15% off retail price when purchased in clinic
                </span>
              </div>
              <p className={classes.whyText}>
                Curious about the full range before you book?
              </p>
              <Link to="/medik8" className={classes.whyLink}>
                Explore Medik8 at Radiance Aesthetics →
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section
        className={`${classes.section} ${classes.sectionAlt}`}
        ref={faqSectionRef}
      >
        <div className={classes.sectionContainer}>
          <p className={classes.sectionLabel}>FAQ</p>
          <h2 className={classes.sectionTitle}>Common Questions</h2>
          <div className={classes.faqContainer}>
            {faqs.map((faq, i) => (
              <div className={classes.faqItem} key={faq.q}>
                <h3
                  className={classes.faqQuestion}
                  onClick={() => {
                    const opening = expandedFaq !== i;
                    trackFaqToggle(faq.q, i, opening);
                    setExpandedFaq(opening ? i : null);
                  }}
                  role="button"
                  aria-expanded={expandedFaq === i}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      const opening = expandedFaq !== i;
                      trackFaqToggle(faq.q, i, opening);
                      setExpandedFaq(opening ? i : null);
                    }
                  }}
                >
                  {faq.q} {expandedFaq === i ? "−" : "+"}
                </h3>
                {expandedFaq === i && (
                  <p className={classes.faqAnswer}>{faq.a}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className={classes.ctaSection} aria-label="Book appointment">
        <h2 className={classes.ctaTitle}>Ready to Find Your Routine?</h2>
        <p className={classes.ctaDescription}>
          Book your free Medik8 consultation today — no cost, no obligation.
        </p>
        <a
          href={BOOKING_URL}
          className={classes.ctaButton}
          target="_blank"
          rel="noreferrer"
          onClick={() => trackBookingClick("Medik8 Consultation Bottom CTA")}
        >
          Book Your Free Consultation
        </a>
      </section>

      <div className={classes.ctaWidgetWrapper}>
        <BookingWidgetSection source="Treatments - Medik8 Consultation" />
      </div>
    </>
  );
};

export default Medik8ConsultationPage;
